import { supabase } from '../lib/supabase';

/**
 * Inventory Service - Handles stock tracking, alerts, and transaction history
 * Database schema: inventory_transactions, low_stock_alerts tables with snake_case columns
 * React uses camelCase - this service handles conversion
 */

export const inventoryService = {
  /**
   * Get inventory transactions for a product
   * @param {string} productId - Product UUID
   * @returns {Promise<Array>} Transaction history
   */
  async getProductTransactions(productId) {
    try {
      const { data, error } = await supabase
        ?.from('inventory_transactions')
        ?.select(`
          *,
          created_by_profile:created_by (
            id,
            full_name,
            email
          )
        `)
        ?.eq('product_id', productId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(transaction => ({
        id: transaction?.id,
        productId: transaction?.product_id,
        transactionType: transaction?.transaction_type,
        quantityChange: transaction?.quantity_change,
        quantityBefore: transaction?.quantity_before,
        quantityAfter: transaction?.quantity_after,
        orderId: transaction?.order_id,
        notes: transaction?.notes,
        createdBy: transaction?.created_by,
        createdByProfile: transaction?.created_by_profile ? {
          id: transaction?.created_by_profile?.id,
          fullName: transaction?.created_by_profile?.full_name,
          email: transaction?.created_by_profile?.email
        } : null,
        createdAt: transaction?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching product transactions:', error);
      throw error;
    }
  },

  /**
   * Get all inventory transactions (admin only)
   * @returns {Promise<Array>} All transactions
   */
  async getAllTransactions() {
    try {
      const { data, error } = await supabase
        ?.from('inventory_transactions')
        ?.select(`
          *,
          product:products (
            id,
            title,
            sku
          ),
          created_by_profile:created_by (
            id,
            full_name
          )
        `)
        ?.order('created_at', { ascending: false })
        ?.limit(100);

      if (error) throw error;

      return data?.map(transaction => ({
        id: transaction?.id,
        productId: transaction?.product_id,
        product: transaction?.product ? {
          id: transaction?.product?.id,
          title: transaction?.product?.title,
          sku: transaction?.product?.sku
        } : null,
        transactionType: transaction?.transaction_type,
        quantityChange: transaction?.quantity_change,
        quantityBefore: transaction?.quantity_before,
        quantityAfter: transaction?.quantity_after,
        orderId: transaction?.order_id,
        notes: transaction?.notes,
        createdBy: transaction?.created_by,
        createdByProfile: transaction?.created_by_profile,
        createdAt: transaction?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching all transactions:', error);
      throw error;
    }
  },

  /**
   * Record manual inventory adjustment
   * @param {Object} adjustmentData - Adjustment details
   * @returns {Promise<Object>} Created transaction
   */
  async recordAdjustment(adjustmentData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      const { data, error } = await supabase
        ?.rpc('record_inventory_transaction', {
          p_product_id: adjustmentData?.productId,
          p_transaction_type: adjustmentData?.transactionType || 'adjustment',
          p_quantity_change: adjustmentData?.quantityChange,
          p_order_id: adjustmentData?.orderId || null,
          p_notes: adjustmentData?.notes || null,
          p_created_by: user?.id
        });

      if (error) throw error;

      return { id: data };
    } catch (error) {
      console.error('Error recording inventory adjustment:', error);
      throw error;
    }
  },

  /**
   * Get active low stock alerts
   * @returns {Promise<Array>} Active alerts
   */
  async getLowStockAlerts() {
    try {
      const { data, error } = await supabase
        ?.from('low_stock_alerts')
        ?.select(`
          *,
          product:products (
            id,
            title,
            sku,
            stock_quantity,
            low_stock_threshold,
            image_urls,
            images
          )
        `)
        ?.eq('alert_status', 'active')
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(alert => ({
        id: alert?.id,
        productId: alert?.product_id,
        product: alert?.product ? {
          id: alert?.product?.id,
          title: alert?.product?.title,
          sku: alert?.product?.sku,
          stockQuantity: alert?.product?.stock_quantity,
          lowStockThreshold: alert?.product?.low_stock_threshold,
          image: alert?.product?.image_urls?.[0]?.url || alert?.product?.images?.[0] || null
        } : null,
        currentStock: alert?.current_stock,
        threshold: alert?.threshold,
        alertStatus: alert?.alert_status,
        createdAt: alert?.created_at,
        updatedAt: alert?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching low stock alerts:', error);
      throw error;
    }
  },

  /**
   * Dismiss a low stock alert
   * @param {string} alertId - Alert UUID
   * @returns {Promise<void>}
   */
  async dismissAlert(alertId) {
    try {
      const { error } = await supabase
        ?.from('low_stock_alerts')
        ?.update({
          alert_status: 'dismissed',
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', alertId);

      if (error) throw error;
    } catch (error) {
      console.error('Error dismissing alert:', error);
      throw error;
    }
  },

  /**
   * Get inventory statistics
   * @returns {Promise<Object>} Inventory stats
   */
  async getInventoryStats() {
    try {
      const { data: products, error: productsError } = await supabase
        ?.from('products')
        ?.select('stock_quantity, low_stock_threshold, track_inventory')
        ?.eq('is_active', true)
        ?.eq('track_inventory', true);

      if (productsError) throw productsError;

      const { data: alerts, error: alertsError } = await supabase
        ?.from('low_stock_alerts')
        ?.select('id')
        ?.eq('alert_status', 'active');

      if (alertsError) throw alertsError;

      const totalProducts = products?.length || 0;
      const lowStockCount = alerts?.length || 0;
      const outOfStockCount = products?.filter(p => p?.stock_quantity === 0)?.length || 0;
      const totalStockValue = products?.reduce((sum, p) => sum + (p?.stock_quantity || 0), 0);

      return {
        totalProducts,
        lowStockCount,
        outOfStockCount,
        totalStockValue,
        healthyStockCount: totalProducts - lowStockCount - outOfStockCount
      };
    } catch (error) {
      console.error('Error fetching inventory stats:', error);
      throw error;
    }
  },

  /**
   * Subscribe to low stock alerts in real-time
   * @param {Function} callback - Callback function for new alerts
   * @returns {Object} Subscription object
   */
  subscribeToAlerts(callback) {
    const subscription = supabase
      ?.channel('low_stock_alerts_channel')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'low_stock_alerts',
          filter: 'alert_status=eq.active'
        },
        (payload) => {
          callback(payload);
        }
      )
      ?.subscribe();

    return subscription;
  },

  /**
   * Check stock availability before purchase
   * @param {Array} cartItems - Items to check
   * @returns {Promise<Object>} Availability status
   */
  async checkStockAvailability(cartItems) {
    try {
      const productIds = cartItems?.map(item => item?.id);
      
      const { data, error } = await supabase
        ?.from('products')
        ?.select('id, title, stock_quantity, track_inventory')
        ?.in('id', productIds);

      if (error) throw error;

      const unavailableItems = [];
      const warnings = [];

      cartItems?.forEach(cartItem => {
        const product = data?.find(p => p?.id === cartItem?.id);
        
        if (!product) {
          unavailableItems?.push({
            id: cartItem?.id,
            title: cartItem?.title,
            reason: 'Ürün bulunamadı'
          });
          return;
        }

        if (product?.track_inventory && product?.stock_quantity < cartItem?.quantity) {
          unavailableItems?.push({
            id: cartItem?.id,
            title: cartItem?.title,
            requestedQuantity: cartItem?.quantity,
            availableQuantity: product?.stock_quantity,
            reason: `Yetersiz stok. Mevcut: ${product?.stock_quantity}, İstenen: ${cartItem?.quantity}`
          });
        } else if (product?.track_inventory && product?.stock_quantity === cartItem?.quantity) {
          warnings?.push({
            id: cartItem?.id,
            title: cartItem?.title,
            message: 'Bu ürünün son stokları'
          });
        }
      });

      return {
        available: unavailableItems?.length === 0,
        unavailableItems,
        warnings
      };
    } catch (error) {
      console.error('Error checking stock availability:', error);
      throw error;
    }
  },

  /**
   * Update stock after order creation using atomic RPC function
   * @param {Array} orderItems - Array of order items with product_id and quantity
   * @returns {Promise<void>}
   * @throws {Error} If insufficient stock for any item
   */
  async updateStockAfterOrder(orderItems) {
    try {
      // Iterate through each item and call RPC function
      for (const item of orderItems) {
        const { data: success, error } = await supabase
          ?.rpc('decrement_stock', {
            p_product_id: item?.product_id || item?.id,
            p_quantity: item?.quantity
          });

        if (error) {
          console.error('Error calling decrement_stock RPC:', error);
          throw new Error(`Stok güncellenirken hata oluştu: ${error.message}`);
        }

        // If RPC returns false, insufficient stock
        if (success === false) {
          throw new Error(`Yetersiz stok: Ürün ID ${item?.product_id || item?.id} için yeterli stok yok`);
        }
      }
    } catch (error) {
      console.error('Error updating stock after order:', error);
      throw error;
    }
  }
};

export default inventoryService;